// hooks/useCacheManager.js
import { useCallback, useEffect, useState } from 'react';

const CACHE_PREFIX = 'competences_cache_';
const DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes par défaut

export const useCacheManager = () => {
  // Fonction pour créer une clé de cache
  const getCacheKey = useCallback((endpoint, params = {}) => {
    const paramsString = Object.keys(params).length > 0 
      ? `_${JSON.stringify(params)}` 
      : '';
    return `${CACHE_PREFIX}${endpoint}${paramsString}`;
  }, []);

  // Fonction pour sauvegarder dans le cache
  const setCacheData = useCallback((key, data, customDuration = DEFAULT_CACHE_DURATION) => {
    try {
      const cacheItem = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + customDuration,
        lastModified: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(cacheItem));
      console.log(`💾 Cache sauvé: ${key}`);
    } catch (error) {
      console.warn('❌ Erreur lors de la sauvegarde du cache:', error);
    }
  }, []);

  // Fonction pour récupérer du cache
  const getCacheData = useCallback((key) => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheItem = JSON.parse(cached);
      const now = Date.now();

      // Vérifier si le cache a expiré
      if (now > cacheItem.expiresAt) {
        localStorage.removeItem(key);
        console.log(`🗑️ Cache expiré supprimé: ${key}`);
        return null;
      }

      console.log(`📦 Cache récupéré: ${key} (${Math.round((cacheItem.expiresAt - now) / 1000)}s restantes)`);
      return cacheItem.data;
    } catch (error) {
      console.warn('❌ Erreur lors de la lecture du cache:', error);
      return null;
    }
  }, []);

  // Fonction pour invalider le cache
  const invalidateCache = useCallback((pattern = null) => {
    try {
      if (pattern) {
        // Invalider un pattern spécifique
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(CACHE_PREFIX) && key.includes(pattern)) {
            localStorage.removeItem(key);
            console.log(`🗑️ Cache invalidé: ${key}`);
          }
        });
      } else {
        // Invalider tout le cache
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(CACHE_PREFIX)) {
            localStorage.removeItem(key);
          }
        });
        console.log('🗑️ Tout le cache invalidé');
      }
    } catch (error) {
      console.warn('❌ Erreur lors de l\'invalidation du cache:', error);
    }
  }, []);

  // Fonction pour vérifier si les données ont été modifiées
  const hasDataChanged = useCallback((key, newData) => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return true;

      const cacheItem = JSON.parse(cached);
      const dataHash = JSON.stringify(newData);
      const cachedHash = JSON.stringify(cacheItem.data);
      
      return dataHash !== cachedHash;
    } catch (error) {
      return true; // En cas d'erreur, considérer que les données ont changé
    }
  }, []);

  // Fonction pour nettoyer le cache expiré
  const cleanExpiredCache = useCallback(() => {
    try {
      const now = Date.now();
      let cleaned = 0;

      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          try {
            const cacheItem = JSON.parse(localStorage.getItem(key));
            if (now > cacheItem.expiresAt) {
              localStorage.removeItem(key);
              cleaned++;
            }
          } catch (error) {
            // Si erreur de parsing, supprimer l'item corrompu
            localStorage.removeItem(key);
            cleaned++;
          }
        }
      });

      if (cleaned > 0) {
        console.log(`🧹 ${cleaned} élément(s) de cache expiré(s) nettoyé(s)`);
      }
    } catch (error) {
      console.warn('❌ Erreur lors du nettoyage du cache:', error);
    }
  }, []);

  // Nettoyer le cache expiré au montage
  useEffect(() => {
    cleanExpiredCache();
  }, [cleanExpiredCache]);

  return {
    getCacheKey,
    setCacheData,
    getCacheData,
    invalidateCache,
    hasDataChanged,
    cleanExpiredCache
  };
};